import type { FC } from 'react';
import { CloudRain, MapPin, SprayCan, Wind } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, num, optStr, str } from '@/app/lib/a2ui/props.ts';

const messages = defineMessages({
  next48: {
    id: 'weatherStrip.next48',
    defaultMessage: 'Next 48 hours',
    description: 'Heading above the two-day forecast blocks used for the spray decision.',
  },
  spraySafe: {
    id: 'weatherStrip.verdictSafe',
    defaultMessage: 'Spray today',
    description: 'Verdict tag: conditions allow spraying today.',
  },
  sprayWait: {
    id: 'weatherStrip.verdictWait',
    defaultMessage: 'Wait',
    description: 'Verdict tag: conditions do not allow spraying yet.',
  },
  window: {
    id: 'weatherStrip.window',
    defaultMessage: 'Clear window',
    description: 'Label before the next weather window suitable for spraying.',
  },
  temp: {
    id: 'weatherStrip.temp',
    defaultMessage: '{value}°C',
    description: 'Temperature reading in degrees Celsius.',
  },
  rain: {
    id: 'weatherStrip.rain',
    defaultMessage: '{value}% rain',
    description: 'Chance of rain as a percentage.',
  },
  wind: {
    id: 'weatherStrip.wind',
    defaultMessage: '{value} km/h',
    description: 'Wind speed in kilometres per hour.',
  },
});

interface Block {
  label: string;
  condition: string;
  tempC?: number;
  rainChance?: number;
  windKmh?: number;
  risky: boolean;
}

function readBlocks(value: unknown): Block[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const label = str(record.label);
    if (!label) {
      return [];
    }
    return [
      {
        label,
        condition: str(record.condition),
        tempC: num(record.tempC),
        rainChance: num(record.rainChance),
        windKmh: num(record.windKmh),
        risky: record.risky === true,
      },
    ];
  });
}

export const WeatherStrip: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const village = str(node.props.village);
  const canSpray = str(node.props.verdict) === 'spray_today';
  const verdictLine = str(node.props.verdictLine);
  const window = optStr(node.props.window);
  const blocks = readBlocks(node.props.blocks);
  const sourceNote = optStr(node.props.sourceNote);

  return (
    <div
      className={`w-full rounded-lg border-2 p-5 ${
        canSpray ? 'border-success/50 bg-success/10' : 'border-warning/50 bg-warning/10'
      }`}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-foreground">
          <MapPin className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 truncate" translate="no">
            {village}
          </span>
        </p>
        <span
          className={`flex shrink-0 items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-caps ${
            canSpray
              ? 'bg-success text-success-foreground'
              : 'bg-warning text-warning-foreground'
          }`}
        >
          {canSpray ? (
            <SprayCan className="size-3.5" aria-hidden />
          ) : (
            <CloudRain className="size-3.5" aria-hidden />
          )}
          {canSpray
            ? intl.formatMessage(messages.spraySafe)
            : intl.formatMessage(messages.sprayWait)}
        </span>
      </div>

      <p className="mb-4 text-lg font-bold leading-snug tracking-tight text-foreground">
        {verdictLine}
      </p>

      {window ? (
        <p className="mb-4 text-base text-foreground">
          <span className="font-semibold text-muted-foreground">
            {intl.formatMessage(messages.window)}:{' '}
          </span>
          {window}
        </p>
      ) : null}

      {blocks.length > 0 ? (
        <>
          <p className="mb-2 text-xs font-semibold uppercase tracking-caps text-muted-foreground">
            {intl.formatMessage(messages.next48)}
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {blocks.map((block) => (
              <div
                key={block.label}
                data-risky={block.risky}
                className="rounded-md border border-border bg-card p-3 data-[risky=true]:border-destructive/60 data-[risky=true]:bg-destructive/10"
              >
                <p className="truncate text-xs font-bold uppercase tracking-caps text-muted-foreground">
                  {block.label}
                </p>
                <p className="mt-1 text-sm font-semibold leading-snug text-foreground">
                  {block.condition}
                </p>
                <p className="mt-1 text-sm tabular-nums text-foreground">
                  {block.tempC !== undefined
                    ? intl.formatMessage(messages.temp, { value: Math.round(block.tempC) })
                    : null}
                </p>
                {block.rainChance !== undefined ? (
                  <p className="text-xs tabular-nums text-muted-foreground">
                    {intl.formatMessage(messages.rain, { value: Math.round(block.rainChance) })}
                  </p>
                ) : null}
                {block.windKmh !== undefined ? (
                  <p className="flex items-center gap-1 text-xs tabular-nums text-muted-foreground">
                    <Wind className="size-3 shrink-0" aria-hidden />
                    {intl.formatMessage(messages.wind, { value: Math.round(block.windKmh) })}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}

      {sourceNote ? (
        <p className="mt-3 text-xs text-muted-foreground-subtle">{sourceNote}</p>
      ) : null}
    </div>
  );
};
